/**
 * AAMC Recognized Medical Specialties
 * 
 * Centralized list of medical specialties recognized by AAMC
 * (Association of American Medical Colleges).
 * 
 * Used across:
 * - Symptom Checker (specialty-specific analysis)
 * - Check-in Form Builder (clinic specialty tagging)
 * - CareMap (specialty-based filtering)
 */

// =============================================================================
// AAMC RECOGNIZED MEDICAL SPECIALTIES
// =============================================================================

/**
 * List of medical specialties recognized by AAMC
 * Each entry has:
 * - value: URL-safe identifier (kebab-case)
 * - label: Human-readable display name
 */
export const AAMC_SPECIALTIES = [
  { value: 'emergency-medicine', label: 'Emergency Medicine' },
  { value: 'allergy-immunology', label: 'Allergy and Immunology' },
  { value: 'anesthesiology', label: 'Anesthesiology' },
  { value: 'cardiology', label: 'Cardiovascular Disease (Cardiology)' },
  { value: 'child-adolescent-psychiatry', label: 'Child and Adolescent Psychiatry' },
  { value: 'colon-rectal-surgery', label: 'Colon and Rectal Surgery' },
  { value: 'critical-care', label: 'Critical Care Medicine' },
  { value: 'dermatology', label: 'Dermatology' },
  { value: 'endocrinology', label: 'Endocrinology, Diabetes and Metabolism' },
  { value: 'family-medicine', label: 'Family Medicine' },
  { value: 'gastroenterology', label: 'Gastroenterology' },
  { value: 'geriatric-medicine', label: 'Geriatric Medicine' },
  { value: 'hematology', label: 'Hematology' },
  { value: 'hospice-palliative', label: 'Hospice and Palliative Medicine' },
  { value: 'infectious-disease', label: 'Infectious Disease' },
  { value: 'internal-medicine', label: 'Internal Medicine' },
  { value: 'interventional-cardiology', label: 'Interventional Cardiology' },
  { value: 'maternal-fetal', label: 'Maternal-Fetal Medicine' },
  { value: 'medical-genetics', label: 'Medical Genetics and Genomics' },
  { value: 'neonatal-perinatal', label: 'Neonatal-Perinatal Medicine' },
  { value: 'nephrology', label: 'Nephrology' },
  { value: 'neurological-surgery', label: 'Neurological Surgery' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'nuclear-medicine', label: 'Nuclear Medicine' },
  { value: 'obstetrics-gynecology', label: 'Obstetrics and Gynecology' },
  { value: 'occupational-medicine', label: 'Occupational Medicine' },
  { value: 'oncology', label: 'Medical Oncology' },
  { value: 'ophthalmology', label: 'Ophthalmology' },
  { value: 'orthopedic-surgery', label: 'Orthopedic Surgery' },
  { value: 'otolaryngology', label: 'Otolaryngology (ENT)' },
  { value: 'pain-medicine', label: 'Pain Medicine' },
  { value: 'pathology', label: 'Pathology' },
  { value: 'pediatrics', label: 'Pediatrics' },
  { value: 'physical-medicine', label: 'Physical Medicine and Rehabilitation' },
  { value: 'plastic-surgery', label: 'Plastic Surgery' },
  { value: 'preventive-medicine', label: 'Preventive Medicine' },
  { value: 'psychiatry', label: 'Psychiatry' },
  { value: 'pulmonology', label: 'Pulmonary Disease' },
  { value: 'radiation-oncology', label: 'Radiation Oncology' },
  { value: 'radiology', label: 'Radiology-Diagnostic' },
  { value: 'reproductive-endocrinology', label: 'Reproductive Endocrinology/Infertility' },
  { value: 'rheumatology', label: 'Rheumatology' },
  { value: 'sleep-medicine', label: 'Sleep Medicine' },
  { value: 'sports-medicine', label: 'Sports Medicine' },
  { value: 'surgery-general', label: 'Surgery-General' },
  { value: 'thoracic-surgery', label: 'Thoracic Surgery' },
  { value: 'urology', label: 'Urology' },
  { value: 'vascular-surgery', label: 'Vascular Surgery' },
];

/**
 * Default specialty for new clinics or unspecified contexts
 */
export const DEFAULT_SPECIALTY = 'emergency-medicine';

/**
 * Get specialty label by value
 * @param {string} value - Specialty value (e.g., 'cardiology')
 * @returns {string} Human-readable label (e.g., 'Cardiovascular Disease (Cardiology)')
 */
export const getSpecialtyLabel = (value) => {
  const specialty = AAMC_SPECIALTIES.find(s => s.value === value);
  return specialty ? specialty.label : 'Emergency Medicine';
};

/**
 * Get specialty value by label (reverse lookup)
 * @param {string} label - Specialty label
 * @returns {string|null} Specialty value or null if not found
 */
export const getSpecialtyValue = (label) => {
  const specialty = AAMC_SPECIALTIES.find(s => s.label === label);
  return specialty ? specialty.value : null;
};

/**
 * Check if a value is a valid specialty
 * @param {string} value - Value to check
 * @returns {boolean} True if valid specialty
 */
export const isValidSpecialty = (value) => {
  return AAMC_SPECIALTIES.some(s => s.value === value);
};

/**
 * Common clinic specialties (subset for quick selection)
 * These are the most commonly used specialties for clinics
 */
export const COMMON_CLINIC_SPECIALTIES = [
  'family-medicine',
  'internal-medicine',
  'pediatrics',
  'obstetrics-gynecology',
  'dermatology',
  'orthopedic-surgery',
  'cardiology',
  'gastroenterology',
  'neurology',
  'psychiatry',
  'ophthalmology',
  'otolaryngology',
  'urology',
  'allergy-immunology',
  'endocrinology',
];

export default {
  AAMC_SPECIALTIES,
  DEFAULT_SPECIALTY,
  getSpecialtyLabel,
  getSpecialtyValue,
  isValidSpecialty,
  COMMON_CLINIC_SPECIALTIES,
};
